// src/pages/SDA_Owner/components/pages/Settings.jsx
import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Lock, Trash2 } from "lucide-react";
import DeleteConfirmDialog from "@/components/modal/DeleteConfirmDialog";

import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";

const ACCESS_TOKEN = "accessToken";

const API_URL = import.meta.env.VITE_API_URL;

// Decode owner id from JWT so each owner can have their own localStorage keys
function getOwnerIdFromToken(token) {
  try {
    const [, payload] = token.split(".");
    const data = JSON.parse(atob(payload));

    // adjust if your JWT structure is different
    return (
      data.id ||
      data.user_id ||
      (data.sub && (data.sub.id || data.sub.user_id || data.sub)) ||
      null
    );
  } catch {
    return null;
  }
}

function getAuthMethodFromToken(token) {
  try {
    const [, payload] = token.split(".");
    const data = JSON.parse(atob(payload));

    return data.auth_method || "";
  } catch {
    return "";
  }
}

function makeStorageKey(ownerId, field) {
  return ownerId ? `sda_owner_${ownerId}_${field}` : `sda_owner_${field}`;
}

function toE164(value) {
  if (!value) return "";
  const v = String(value).trim();

  // already E.164 (+ then digits only)
  if (/^\+\d+$/.test(v)) return v;

  // remove spaces, dashes, etc.
  const digits = v.replace(/\D/g, "");
  if (!digits) return "";

  // if user typed +, keep it
  return v.startsWith("+") ? `+${digits}` : digits;
}

function evaluatePassword(pw) {
  const lengthOK = pw.length >= 6;
  const hasLower = /[a-z]/.test(pw);
  const hasUpper = /[A-Z]/.test(pw);
  const hasNumber = /\d/.test(pw);
  const hasSpecial = /[^A-Za-z0-9]/.test(pw);

  const score =
    (lengthOK ? 1 : 0) +
    (hasLower ? 1 : 0) +
    (hasUpper ? 1 : 0) +
    (hasNumber ? 1 : 0) +
    (hasSpecial ? 1 : 0);

  let label = "";
  let color = "";

  if (!pw) {
    label = "";
    color = "";
  } else if (score <= 2) {
    label = "Weak";
    color = "bg-red-500";
  } else if (score === 3) {
    label = "Moderate";
    color = "bg-yellow-500";
  } else {
    label = "Strong";
    color = "bg-green-500";
  }

  return { score, label, color };
}

function SettingsPage() {
  // Profile info
  const [fullName, setFullName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  // Profile save state
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMessage, setProfileMessage] = useState("");
  const [profileError, setProfileError] = useState("");

  // Password panel state
  const [showPasswordPanel, setShowPasswordPanel] = useState(false);
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [newPasswordStrength, setNewPasswordStrength] = useState({
    score: 0,
    label: "",
    color: "",
  });

  // Delete account state
  const [deleting, setDeleting] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  // ---------------------------
  // Initial load of profile
  // ---------------------------
  useEffect(() => {
    const token =
      localStorage.getItem(ACCESS_TOKEN) ||
      sessionStorage.getItem(ACCESS_TOKEN);

    if (!token) return;

    const authMethod = getAuthMethodFromToken(token);

    if (authMethod === "magic_link") {
      setRecoveryMode(true);
      setShowPasswordPanel(true);
    }

    const ownerId = getOwnerIdFromToken(token);

    const nameKey = makeStorageKey(ownerId, "full_name");
    const usernameKey = makeStorageKey(ownerId, "username");
    const emailKey = makeStorageKey(ownerId, "email");
    const phoneKey = makeStorageKey(ownerId, "phone");

    // Prefill from storage first
    const storedFullName =
      localStorage.getItem(nameKey) || sessionStorage.getItem(nameKey) || "";
    const storedUsername =
      localStorage.getItem(usernameKey) ||
      sessionStorage.getItem(usernameKey) ||
      "";
    const storedEmail =
      localStorage.getItem(emailKey) || sessionStorage.getItem(emailKey) || "";
    const storedPhone =
      localStorage.getItem(phoneKey) || sessionStorage.getItem(phoneKey) || "";

    if (storedFullName) setFullName(storedFullName);
    if (storedUsername) setUsername(storedUsername);
    if (storedEmail) setEmail(storedEmail);
    if (storedPhone) setPhone(toE164(storedPhone));

    // Then refresh from backend
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/sdaowner/me`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) return;

        const data = await res.json();

        const nameFromApi = data.full_name || storedFullName || "";
        const usernameFromApi = data.username || storedUsername || "";
        const emailFromApi = data.email || storedEmail || "";
        const phoneFromApi =
          data.phone || data.phone_number || storedPhone || "";

        setFullName(nameFromApi);
        setUsername(usernameFromApi);
        setEmail(emailFromApi);
        setPhone(toE164(phoneFromApi));

        localStorage.setItem(usernameKey, usernameFromApi);
        sessionStorage.setItem(usernameKey, usernameFromApi);

        localStorage.setItem(emailKey, emailFromApi);
        sessionStorage.setItem(emailKey, emailFromApi);

        localStorage.setItem(phoneKey, toE164(phoneFromApi));
        sessionStorage.setItem(phoneKey, toE164(phoneFromApi));
      } catch (err) {
        console.error("Failed to load SDA owner profile", err);
      }
    })();
  }, []);

  // ---------------------------
  // Save profile (name/email/phone)
  // ---------------------------
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    setProfileMessage("");
    setProfileError("");

    const token =
      localStorage.getItem(ACCESS_TOKEN) ||
      sessionStorage.getItem(ACCESS_TOKEN);

    if (!token) {
      setProfileError("You are not logged in.");
      setSavingProfile(false);
      return;
    }

    const ownerId = getOwnerIdFromToken(token);
    const nameKey = makeStorageKey(ownerId, "full_name");
    const usernameKey = makeStorageKey(ownerId, "username");
    const emailKey = makeStorageKey(ownerId, "email");
    const phoneKey = makeStorageKey(ownerId, "phone");

    const bodyPayload = {
      full_name: fullName,
      username,
      email,
      phone: toE164(phone),
    };

    try {
      const res = await fetch(`${API_URL}/api/sdaowner/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(bodyPayload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setProfileError(data.message || "Failed to save changes.");
      } else {
        const updatedUser = data.user || {};
        const updatedFullName = updatedUser.full_name ?? fullName;
        const updatedUsername = updatedUser.username ?? username;
        const updatedEmail = updatedUser.email ?? email;
        const updatedPhoneRaw =
          updatedUser.phone ?? updatedUser.phone_number ?? phone;

        setFullName(updatedFullName);
        setUsername(updatedUsername);
        setEmail(updatedEmail);
        setPhone(toE164(updatedPhoneRaw));

        // Update storage for this owner (store full phone)
        localStorage.setItem(nameKey, updatedFullName);
        sessionStorage.setItem(nameKey, updatedFullName);

        localStorage.setItem(usernameKey, updatedUsername);
        sessionStorage.setItem(usernameKey, updatedUsername);

        localStorage.setItem(emailKey, updatedEmail);
        sessionStorage.setItem(emailKey, updatedEmail);

        localStorage.setItem(phoneKey, toE164(updatedPhoneRaw));
        sessionStorage.setItem(phoneKey, toE164(updatedPhoneRaw));

        setProfileMessage(data.message || "Profile updated successfully.");
      }
    } catch (err) {
      console.error("Save profile error", err);
      setProfileError("Network error. Please try again.");
    } finally {
      setSavingProfile(false);
    }
  };

  // ---------------------------
  // Change password
  // ---------------------------
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMessage("");
    setPasswordError("");

    if (!newPassword || !confirmPassword) {
      setPasswordError("Please enter and confirm your new password.");
      return;
    }

    if (!recoveryMode && !currentPassword) {
      setPasswordError("Please enter your current password.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("New password and confirmation do not match.");
      return;
    }

    const strength = evaluatePassword(newPassword);

    if (newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters.");
      return;
    }

    if (strength.score < 3) {
      setPasswordError(
        "Password is too weak. Use upper & lower case letters and at least one number or symbol.",
      );
      return;
    }

    const token =
      localStorage.getItem(ACCESS_TOKEN) ||
      sessionStorage.getItem(ACCESS_TOKEN);

    if (!token) {
      setPasswordError("You are not logged in.");
      return;
    }

    setSavingPassword(true);

    try {
      const res = await fetch(`${API_URL}/api/sdaowner/change_password`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...(recoveryMode ? {} : { current_password: currentPassword }),
          new_password: newPassword,
          confirm_password: confirmPassword,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setPasswordError(data.message || "Failed to change password.");
      } else {
        setPasswordMessage(data.message || "Password updated successfully.");
        setRecoveryMode(false);
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setNewPasswordStrength({ score: 0, label: "", color: "" });
        setShowPasswordPanel(false);
      }
    } catch (err) {
      console.error("Change password error", err);
      setPasswordError("Network error. Please try again.");
    } finally {
      setSavingPassword(false);
    }
  };

  // ---------------------------
  // Delete account (opens modal + confirms)
  // ---------------------------
  const handleDeleteClick = () => {
    setDeleteError("");
    setOpenDeleteDialog(true);
  };

  const handleConfirmDeleteAccount = async () => {
    setDeleteError("");

    const token =
      localStorage.getItem(ACCESS_TOKEN) ||
      sessionStorage.getItem(ACCESS_TOKEN);

    if (!token) {
      setDeleteError("You are not logged in.");
      setOpenDeleteDialog(false);
      return;
    }

    setDeleting(true);

    try {
      const res = await fetch(`${API_URL}/api/sdaowner/delete_account`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setDeleteError(data.message || "Failed to delete account.");
      } else {
        // Clear local/session storage and redirect to login
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = "/login";
      }
    } catch (err) {
      console.error("Delete account error", err);
      setDeleteError("Network error. Please try again.");
    } finally {
      setDeleting(false);
      setOpenDeleteDialog(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-5 sm:space-y-6 px-3 sm:px-0 pb-24 sm:pb-0">
      {/* PROFILE CARD */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
        <div className="flex items-start sm:items-center gap-4 px-4 sm:px-8 pt-5 sm:pt-8 pb-4 border-b border-gray-100">
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-900">
              Personal Details
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Manage your personal information for your SDA Owner account.
            </p>
          </div>
        </div>

        <form
          onSubmit={handleSaveProfile}
          className="px-4 sm:px-8 pb-6 sm:pb-8 pt-5 sm:pt-6 space-y-5 sm:space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full name
              </label>
              <Input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Your full name"
                className="h-11"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Username
              </label>
              <Input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Username"
                className="h-11"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="h-11"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone
              </label>
              <div className="phone-input w-full">
                <PhoneInput
                  international
                  defaultCountry="AU"
                  value={phone || undefined}
                  onChange={(val) => setPhone(toE164(val || ""))}
                />
              </div>
            </div>
          </div>

          {profileError && (
            <p className="text-sm text-red-500">{profileError}</p>
          )}
          {profileMessage && (
            <p className="text-sm text-green-600">{profileMessage}</p>
          )}

          {/* Desktop buttons (keep normal layout on desktop) */}
          <div className="hidden sm:flex sm:flex-row gap-3 sm:items-center sm:justify-between pt-2">
            <Button
              type="button"
              variant="outline"
              className="rounded-full px-5 w-full sm:w-auto"
              onClick={() =>
                (window.location.href =
                  "mailto:support@nurseassist247.com?subject=SDA%20Owner%20Portal%20Issue")
              }>
              Report an issue
            </Button>

            <Button
              type="submit"
              className="bg-[#D2138C] hover:bg-[#b01173] rounded-full px-8 w-full sm:w-auto"
              disabled={savingProfile}>
              {savingProfile ? "Saving..." : "Save changes"}
            </Button>
          </div>

          {/* Mobile sticky bottom action bar */}
          <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-3 py-3 z-50">
            <div className="max-w-5xl mx-auto flex gap-3">
              <Button
                type="button"
                variant="outline"
                className="rounded-full w-1/2"
                onClick={() =>
                  (window.location.href =
                    "mailto:support@nurseassist247.com?subject=SDA%20Owner%20Portal%20Issue")
                }>
                Report issue
              </Button>

              <Button
                type="submit"
                className="bg-[#D2138C] hover:bg-[#b01173] rounded-full w-1/2"
                disabled={savingProfile}>
                {savingProfile ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        </form>
      </div>

      {/* PASSWORD & AUTH CARD */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
        <div className="px-4 sm:px-8 py-5 sm:py-6 border-b border-gray-100 flex items-start sm:items-center gap-3">
          <Lock className="h-5 w-5 text-[#D2138C]" />
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Password and Authentication
            </h3>
            <p className="text-sm text-gray-500">
              Keep your account secure by updating your password regularly.
            </p>
          </div>
        </div>

        <div className="px-4 sm:px-8 py-5 sm:py-6 space-y-4">
          <Button
            type="button"
            variant="outline"
            className="rounded-full px-6 w-full sm:w-auto"
            onClick={() => {
              setShowPasswordPanel((v) => !v);
              setPasswordMessage("");
              setPasswordError("");
            }}>
            Change password
          </Button>

          {showPasswordPanel && (
            <form
              onSubmit={handleChangePassword}
              className="mt-4 border border-gray-200 rounded-xl p-4 sm:p-6 bg-gray-50">
              <div className="space-y-4">
                {!recoveryMode && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Current password
                    </label>

                    <Input
                      type="password"
                      name="current_password"
                      autoComplete="current-password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="h-11"
                    />

                    <button
                      type="button"
                      onClick={() => {
                        window.location.href = "/forgot-password";
                      }}
                      className="mt-2 text-sm text-[#D2138C] hover:underline">
                      Forgot your current password?
                    </button>
                  </div>
                )}

                {recoveryMode && (
                  <div className="rounded-xl border border-[#D2138C]/20 bg-pink-50 p-4">
                    <p className="text-sm font-semibold text-gray-900">
                      Password update required
                    </p>

                    <p className="text-sm text-gray-700 mt-1">
                      You signed in using a secure password reset link. Please
                      update your password immediately to secure your account.
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New password
                  </label>
                  <Input
                    type="password"
                    name="new_password"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => {
                      const value = e.target.value;
                      setNewPassword(value);
                      setNewPasswordStrength(evaluatePassword(value));
                    }}
                    className="h-11"
                  />

                  {/* strength bar */}
                  {newPasswordStrength.label && (
                    <div className="mt-1">
                      <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${newPasswordStrength.color} transition-all`}
                          style={{
                            width: `${(newPasswordStrength.score / 4) * 100}%`,
                          }}
                        />
                      </div>
                      <p className="mt-1 text-xs text-gray-600">
                        Password strength:{" "}
                        <span className="font-medium">
                          {newPasswordStrength.label}
                        </span>
                      </p>
                    </div>
                  )}

                  <p className="mt-1 text-xs text-gray-500">
                    Must be at least 6 characters and include upper &amp; lower
                    case letters and at least one number or special symbol.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm new password
                  </label>
                  <Input
                    type="password"
                    name="confirm_new_password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="h-11"
                  />
                </div>
              </div>

              {passwordError && (
                <p className="text-sm text-red-500 mt-3">{passwordError}</p>
              )}
              {passwordMessage && (
                <p className="text-sm text-green-600 mt-3">{passwordMessage}</p>
              )}

              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-full px-5 w-full sm:w-auto"
                  onClick={() => {
                    setShowPasswordPanel(false);

                    if (!recoveryMode) {
                      setRecoveryMode(false);
                    }

                    setCurrentPassword("");
                    setNewPassword("");
                    setConfirmPassword("");
                    setNewPasswordStrength({ score: 0, label: "", color: "" });
                    setPasswordError("");
                    setPasswordMessage("");
                  }}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="rounded-full px-6 bg-[#D2138C] hover:bg-[#b01173] w-full sm:w-auto"
                  disabled={savingPassword}>
                  {savingPassword ? "Updating…" : "Update password"}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* ACCOUNT REMOVAL CARD */}
      <div className="bg-white rounded-2xl shadow-sm border border-rose-100">
        <div className="px-4 sm:px-8 py-5 sm:py-6 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-start gap-3 w-full">
            <Trash2 className="h-5 w-5 text-rose-600 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold text-rose-700">
                Account removal
              </h3>
              <p className="text-sm text-rose-500">
                Deleting your account is permanent and cannot be undone. All
                your accommodations and data will be removed.
              </p>
            </div>
          </div>
          <Button
            type="button"
            variant="destructive"
            className="bg-rose-600 hover:bg-rose-700 rounded-full px-6 w-full sm:w-auto"
            onClick={handleDeleteClick}
            disabled={deleting}>
            {deleting ? "Deleting…" : "Delete account"}
          </Button>
        </div>
        {deleteError && (
          <p className="px-4 sm:px-8 pb-4 text-sm text-rose-600">
            {deleteError}
          </p>
        )}
      </div>

      {/* ⬇⬇ Add this here, before the outermost closing div */}
      <DeleteConfirmDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={handleConfirmDeleteAccount}
        title="Delete account"
        description="Are you sure you want to delete your account? This action cannot be undone."
      />
    </div>
  );
}

export default SettingsPage;
